import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { OverviewChartLoading } from '~/modules/common/charts/dashboard';
import OverviewItem from '~/modules/common/charts/dashboard/OverviewChart/OverviewItem';
import { useProjectKPISummary } from './hooks';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex'
  }
}));

const safeRatio = (n, d) => (d ? n / d : null);

export const ProjectKPIChart = ({ chartDataInput }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    projectId,
    dateRange,
    projectPermissions: { canViewTasks }
  } = chartDataInput;

  const {
    teamMembersTimesheetCountSummary,
    billableNonBillableHoursSummary,
    tasksCompletionCountSummary,
    loading
  } = useProjectKPISummary({
    projectId,
    dateRange
  });

  const kpiPercentages = useMemo(
    () => ({
      tci: safeRatio(
        (teamMembersTimesheetCountSummary?.waitingTimesheetCount ?? 0) +
          (teamMembersTimesheetCountSummary?.approvedTimesheetCount ?? 0),
        teamMembersTimesheetCountSummary?.totalTimesheetCount
      ),
      bi: safeRatio(
        billableNonBillableHoursSummary?.billableHours,
        billableNonBillableHoursSummary?.totalHours
      ),
      spi: safeRatio(
        tasksCompletionCountSummary?.closedOnOrBeforeTaskEndDateCount,
        tasksCompletionCountSummary?.totalTaskCount
      )
    }),
    [
      teamMembersTimesheetCountSummary,
      billableNonBillableHoursSummary,
      tasksCompletionCountSummary
    ]
  );

  if (loading) return <OverviewChartLoading />;

  return (
    <Grid container className={classes.container} spacing={2}>
      <Grid item lg={4} md={4} xs={12}>
        <OverviewItem
          title={formatMessage({
            id: 'projectKPI.timesheetComplianceIndex'
          })}
          value={kpiPercentages.tci}
          isPercentage
          dataQeId="TimesheetComplianceIndexKPI"
        />
      </Grid>
      <Grid item lg={4} md={4} xs={12}>
        <OverviewItem
          title={formatMessage({
            id: 'projectKPI.billabilityIndex'
          })}
          value={kpiPercentages.bi}
          isPercentage
          dataQeId="BillabilityIndexKPI"
        />
      </Grid>
      {canViewTasks && (
        <Grid item lg={4} md={4} xs={12}>
          <OverviewItem
            title={formatMessage({
              id: 'projectKPI.schedulePerformanceIndex'
            })}
            value={kpiPercentages.spi}
            isPercentage
            dataQeId="SchedulePerformanceIndexKPI"
          />
        </Grid>
      )}
    </Grid>
  );
};

ProjectKPIChart.propTypes = {
  chartDataInput: PropTypes.shape({
    projectId: PropTypes.string.isRequired,
    dateRange: PropTypes.object,
    projectPermissions: PropTypes.object
  }).isRequired
};

export default ProjectKPIChart;
